import React from 'react';
import Text from '@root/components/v2/ui-elements/Text';
import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
import { Sheet, SheetContent } from '@root/components/v3/sheet';
import { STATUS_COLORS, UI_COLORS } from '@root/utilities/pastelColors';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { StatusPill, formatNumber } from './vulnerabilityScanColumns';
import SeverityBreakdownCard from './SeverityBreakdownCard';
import ChangeSinceLastScanCard from './ChangeSinceLastScanCard';
import WorkloadCoverageCard from './WorkloadCoverageCard';
import { Grid } from '@mui/material';

const StatTile = ({ label, value, tag, muted }) => (
  <div className="flex flex-1 flex-col gap-1 px-5 py-4" style={{ minWidth: 0 }}>
    <div className="flex items-center gap-2">
      <Text
        className="text-[12px] uppercase tracking-wide"
        font="bold"
        element="span"
        style={{ color: UI_COLORS.text.secondary, letterSpacing: '0.06em' }}>
        {label}
      </Text>
      {tag}
    </div>
    <Text
      className="text-[21px] leading-none"
      font="bold"
      style={{ color: muted ? UI_COLORS.text.tertiary : UI_COLORS.text.primary }}>
      {value}
    </Text>
  </div>
);

const SoonTag = () => (
  <span
    className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase"
    style={{ backgroundColor: `${STATUS_COLORS.pending}26`, color: STATUS_COLORS.pending, letterSpacing: '0.05em' }}>
    Soon
  </span>
);

const MetaItem = ({ label, value }) => (
  <span className="flex items-center gap-1.5">
    <Text className="text-[12px]" font="bold" element="span" style={{ color: UI_COLORS.text.secondary }}>
      {label}
    </Text>
    <Text className="text-[12px]" font="medium" element="span" style={{ color: UI_COLORS.text.secondary }}>
      {value ?? '-'}
    </Text>
  </span>
);

const VulnerabilityScanSideOverlay = ({ open, onClose, selectedRow }) => {
  if (!selectedRow) return null;

  const scan = selectedRow;

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent
        side="right"
        hideClose={true}
        className="w-[980px] !max-w-[100%]"
        style={{
          height: '100dvh',
          padding: 0,
          gap: 0,
          borderLeft: `1px solid ${UI_COLORS.border.secondary}`,
          backgroundColor: UI_COLORS.background.tertiary,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <div
          className="flex items-start justify-between gap-4 border-b px-6 py-4"
          style={{ borderColor: UI_COLORS.border.secondary, backgroundColor: UI_COLORS.background.primary }}>
          <div>
            <div className="flex items-center gap-3">
              <Text className="text-[18px]" font="extra-bold" style={{ color: UI_COLORS.text.primary }}>
                {scan.cluster_name} · vulnerability scan
              </Text>
              <StatusPill status={scan.status} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <MetaItem label="Trigger: " value={scan.trigger} />
              <MetaItem label="Region: " value={scan.region} />
              <MetaItem label="Scan ID: " value={scan.scan_id} />
              <MetaItem label="Scan Time: " value={DateFormatPipe(scan.scan_timestamp)} />
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <CircularCloseButton />
          </div>
        </div>

        <div className="h-[calc(100dvh-138px)] w-full bg-white overflow-auto CustomScrollbar p-4">
          <div className="flex flex-col gap-4">
            <div
              className="flex rounded-lg border"
              style={{ borderColor: UI_COLORS.border.secondary, backgroundColor: UI_COLORS.background.primary }}>
              <StatTile label="Workloads" value={formatNumber(scan.workloads)} />
              <StatTile label="Scanned" value={formatNumber(scan.scanned)} />
              <StatTile label="Findings" value={formatNumber(scan.findings)} />
              <StatTile label="Errors" value={0} tag={<SoonTag />} muted />
            </div>
            <Grid container spacing={1.7}>
              <Grid item xs={12} md={6}>
                <SeverityBreakdownCard severity={scan.severity} total={scan.findings} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ChangeSinceLastScanCard change={scan.change} />
              </Grid>
            </Grid>
            <WorkloadCoverageCard workloads={scan.workload_coverage} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default VulnerabilityScanSideOverlay;
